import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical, Play } from 'lucide-react';
import { SortableList } from './SortableList';
import { Dropzone } from './Dropzone';
import { ItemActions } from './ItemActions';
import { PreviewModal } from './PreviewModal';

const VideosManager = () => {
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchVideos = async () => {
    const res = await adminApi.get('/videos');
    setVideos(res.data || []);
  };
  useEffect(() => { fetchVideos(); }, []);

  const blank = () => ({
    thumbnail: '',
    duration: '',
    type: 'Documentary',
    title: '',
    description: '',
    video_url: '',
    order: videos.length,
    published: true,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing.id && videos.find((x) => x.id === editing.id)) {
        await adminApi.put(`/videos/${editing.id}`, editing);
      } else {
        await adminApi.post('/videos', { ...editing, id: undefined });
      }
      toast({ title: 'Saved' });
      setEditing(null);
      fetchVideos();
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete video?')) return;
    await adminApi.delete(`/videos/${id}`);
    fetchVideos();
  };

  const handleReorder = async (reordered) => {
    setVideos(reordered);
    try { await adminApi.put('/videos/reorder', { ids: reordered.map((r) => r.id) }); } catch {}
  };

  return (
    <div className="space-y-6" data-testid="videos-manager">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Videos</h2>
        <Button onClick={() => setEditing(blank())} className="bg-[#1565C0]" data-testid="add-video-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Video
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader><CardTitle>{editing.id ? 'Edit Video' : 'New Video'}</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label htmlFor="video-title">Title</Label>
                <Input id="video-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} required data-testid="video-title-input" />
              </div>
              <div>
                <Label htmlFor="video-description">Description</Label>
                <Textarea id="video-description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="video-type">Type</Label>
                  <Input id="video-type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} placeholder="Documentary" />
                </div>
                <div>
                  <Label htmlFor="video-duration">Duration</Label>
                  <Input id="video-duration" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} placeholder="4:12" />
                </div>
              </div>
              <div>
                <Label htmlFor="video-url">Video URL (YouTube link or uploaded file)</Label>
                <Input id="video-url" value={editing.video_url || ''} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} placeholder="https://youtube.com/..." />
                <p className="text-xs text-gray-500 mt-1">Or upload an mp4 below.</p>
                <div className="mt-2">
                  <Dropzone compact accept="video/*" onUpload={(url) => setEditing({ ...editing, video_url: url })} label="Drop video file" />
                </div>
              </div>
              <div>
                <Label htmlFor="video-thumbnail">Thumbnail Image</Label>
                {editing.thumbnail && <img src={editing.thumbnail} className="w-40 h-24 object-cover rounded mb-2" alt="" />}
                <Dropzone compact onUpload={(url) => setEditing({ ...editing, thumbnail: url })} label="Drop thumbnail image" />
              </div>
              <div className="flex items-center gap-2">
                <Switch id="video-published" checked={editing.published !== false} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                <Label htmlFor="video-published">Published</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#1565C0]" data-testid="save-video-btn">{loading ? 'Saving…' : 'Save'}</Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList
        items={videos}
        onReorder={handleReorder}
        renderItem={(v) => (
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
              <div className="w-24 h-16 bg-gray-100 rounded overflow-hidden relative flex-shrink-0">
                {v.thumbnail ? <img src={v.thumbnail} className="w-full h-full object-cover" alt="" /> : <Play className="w-6 h-6 text-gray-400 m-auto" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">{v.type} • {v.duration}</p>
                <h3 className="font-semibold truncate">{v.title}</h3>
              </div>
              <ItemActions
                collection="videos"
                item={v}
                onChanged={fetchVideos}
                onEdit={setEditing}
                onDelete={handleDelete}
                onPreview={setPreviewing}
              />
            </CardContent>
          </Card>
        )}
      />
      <PreviewModal kind="video" item={previewing} onClose={() => setPreviewing(null)} />
      {videos.length === 0 && !editing && <p className="text-center text-gray-500 py-8">No videos yet.</p>}
    </div>
  );
};

export default VideosManager;
